import { hexToRgb } from './colors';

/** CIE L*a*b*: L* on 0..100, with a* and b* in Lab units. */
export interface LabColor { l: number; a: number; b: number }

export const COLOR_DIFFERENCE_METHOD = {
  version: 'ciede2000-srgb-d50-v1',
  name: 'CIEDE2000 (ΔE00)',
  colorSpace: 'Opaque 8-bit sRGB → CIE XYZ → CIE Lab',
  whitePoint: 'D50',
  observer: 'CIE 1931 2°',
  adaptation: 'Bradford D65 → D50',
  parameters: { kL: 1, kC: 1, kH: 1 },
  sources: [
    'https://www.w3.org/TR/css-color-4/#color-conversion-code',
    'https://hajim.rochester.edu/ece/sites/gsharma/ciede2000/',
  ],
} as const;

/** W3C CSS Color 4 sRGB matrix, Bradford adaptation, and D50 xy white. */
export function hexToLab(hex: string): LabColor {
  const value = hex.trim().replace(/^#/, '');
  const expanded = value.length === 3 ? value.split('').map(c => c + c).join('') : value;
  const rgb = hexToRgb(expanded);
  if (!rgb) throw new TypeError('Expected an opaque 3- or 6-digit sRGB HEX color');
  const [r, g, b] = [rgb.r, rgb.g, rgb.b].map(channel => {
    const c = channel / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  const x = 506752 / 1228815 * r + 87881 / 245763 * g + 12673 / 70218 * b;
  const y = 87098 / 409605 * r + 175762 / 245763 * g + 12673 / 175545 * b;
  const z = 7918 / 409605 * r + 87881 / 737289 * g + 1001167 / 1053270 * b;
  const d50 = [0.3457 / 0.3585, 1, (1 - 0.3457 - 0.3585) / 0.3585];
  const adapted = [
    1.0479297925449969 * x + 0.022946870601609652 * y - 0.05019226628920524 * z,
    0.02962780877005599 * x + 0.9904344267538799 * y - 0.017073799063418826 * z,
    -0.009243040646204504 * x + 0.015055191490298152 * y + 0.7518742814281371 * z,
  ];
  const [fx, fy, fz] = adapted.map((channel, i) => {
    const t = channel / d50[i];
    return t > 216 / 24389 ? Math.cbrt(t) : ((24389 / 27) * t + 16) / 116;
  });
  return { l: 116 * fy - 16, a: 500 * (fx - fy), b: 200 * (fy - fz) };
}

const radians = (degrees: number) => degrees * Math.PI / 180;
const hue = (a: number, b: number) => (Math.atan2(b, a) * 180 / Math.PI + 360) % 360;

/** Sharma, Wu & Dalal (2005), with the standard unit parametric factors.
 * Both inputs must use the same white point and observer. This is a distance,
 * not a similarity percentage, universal visibility threshold, or WCAG test.
 */
export function deltaE2000(first: LabColor, second: LabColor): number {
  if (![first.l, first.a, first.b, second.l, second.a, second.b].every(Number.isFinite)) {
    throw new TypeError('CIE Lab components must be finite numbers');
  }
  const meanChroma = (Math.hypot(first.a, first.b) + Math.hypot(second.a, second.b)) / 2;
  const chroma7 = meanChroma ** 7;
  const g = 0.5 * (1 - Math.sqrt(chroma7 / (chroma7 + 25 ** 7)));
  const a1 = (1 + g) * first.a, a2 = (1 + g) * second.a;
  const c1 = Math.hypot(a1, first.b), c2 = Math.hypot(a2, second.b);
  const h1 = c1 === 0 ? 0 : hue(a1, first.b);
  const h2 = c2 === 0 ? 0 : hue(a2, second.b);
  const dl = second.l - first.l, dc = c2 - c1;
  let dh = h2 - h1;
  if (c1 * c2 === 0) dh = 0;
  else if (dh > 180) dh -= 360;
  else if (dh < -180) dh += 360;
  const dH = 2 * Math.sqrt(c1 * c2) * Math.sin(radians(dh / 2));
  const meanL = (first.l + second.l) / 2, meanC = (c1 + c2) / 2;
  let meanH = h1 + h2;
  if (c1 * c2 !== 0) {
    if (Math.abs(h1 - h2) <= 180) meanH /= 2;
    else meanH = (meanH + (meanH < 360 ? 360 : -360)) / 2;
  }
  const t = 1 - 0.17 * Math.cos(radians(meanH - 30)) + 0.24 * Math.cos(radians(2 * meanH))
    + 0.32 * Math.cos(radians(3 * meanH + 6)) - 0.20 * Math.cos(radians(4 * meanH - 63));
  const sl = 1 + 0.015 * (meanL - 50) ** 2 / Math.sqrt(20 + (meanL - 50) ** 2);
  const sc = 1 + 0.045 * meanC, sh = 1 + 0.015 * meanC * t;
  const meanC7 = meanC ** 7;
  const rotation = -2 * Math.sqrt(meanC7 / (meanC7 + 25 ** 7))
    * Math.sin(radians(60 * Math.exp(-(((meanH - 275) / 25) ** 2))));
  const lightness = dl / sl, chroma = dc / sc, hueDifference = dH / sh;
  return Math.sqrt(Math.max(0, lightness ** 2 + chroma ** 2 + hueDifference ** 2 + rotation * chroma * hueDifference));
}

export function colorDifference(hexA: string, hexB: string): number {
  return deltaE2000(hexToLab(hexA), hexToLab(hexB));
}
